// src/components/CookAI/Hero/HeroSection.tsx
import React from "react";
import { Sparkles, Star, ChevronDown } from "lucide-react";
import { ColorTheme } from "../../../themes/colors";
import { getHeroStyles } from "../../../themes/styles/hero";
import { getCommonStyles } from "../../../themes/styles/common";
import ScreenshotsGrid from "./ScreenshotsGrid";
import { useLanguage } from "../../../i18n/LanguageContext";

const APP_STORE_URL = "https://apps.apple.com/us/app/cook-ai-hey-chef/id6758898666";

interface HeroSectionProps {
    theme: ColorTheme;
    isMobile: boolean;
}

const avatarEmojis = ["👩‍🍳", "🧑‍🍳", "👨‍🍳", "👩🏽‍🍳"];

const HeroSection: React.FC<HeroSectionProps> = ({ theme, isMobile }) => {
    const heroStyles = getHeroStyles(theme);
    const commonStyles = getCommonStyles(theme);
    const { t } = useLanguage();

    const stats = [
        { number: "50K+", label: t.hero.statsRecipes },
        { number: "4.9★", label: t.hero.statsRating },
        { number: "100%", label: t.hero.statsFree },
    ];

    const scrollToHowItWorks = (e: React.MouseEvent) => {
        e.preventDefault();
        document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            id="home"
            style={{
                ...heroStyles.heroSection,
                position: "relative",
                overflow: "visible",
                ...(isMobile ? heroStyles.mobileHero : {}),
            }}
        >
            {/* ── Decorative background: gradient blobs + dot grid ── */}
            <div aria-hidden style={{
                position: "absolute",
                inset: 0,
                overflow: "hidden",
                pointerEvents: "none",
                zIndex: 0,
            }}>
                <div style={{
                    position: "absolute",
                    top: "-120px",
                    left: "-140px",
                    width: "520px",
                    height: "520px",
                    background: `radial-gradient(circle at center, ${theme.primary}26 0%, transparent 65%)`,
                    borderRadius: "50%",
                    filter: "blur(8px)",
                    animation: "blobDrift 14s ease-in-out infinite",
                }} />
                <div style={{
                    position: "absolute",
                    bottom: "-160px",
                    right: "-120px",
                    width: "560px",
                    height: "560px",
                    background: `radial-gradient(circle at center, ${theme.secondary}22 0%, transparent 65%)`,
                    borderRadius: "50%",
                    filter: "blur(8px)",
                    animation: "blobDrift 18s ease-in-out infinite reverse",
                }} />
                <div style={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: `radial-gradient(${theme.textPrimary}12 1px, transparent 1px)`,
                    backgroundSize: "28px 28px",
                    maskImage: "radial-gradient(ellipse 75% 65% at 50% 40%, black 30%, transparent 75%)",
                    WebkitMaskImage: "radial-gradient(ellipse 75% 65% at 50% 40%, black 30%, transparent 75%)",
                    opacity: 0.5,
                }} />
            </div>

            {/* ── Left Side – Hero Content ── */}
            <div style={{ ...heroStyles.heroLeft, position: "relative", zIndex: 1 }}>

                {/* Badge */}
                <div className="hero-stagger" style={{ animationDelay: "0ms" }}>
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "7px 16px",
                            background: theme.badgeBackground,
                            borderRadius: "9999px",
                            color: theme.badgeText,
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            marginBottom: "1.5rem",
                            letterSpacing: "0.04em",
                            textTransform: "uppercase" as const,
                            border: `1px solid ${theme.primary}35`,
                            boxShadow: `0 4px 16px ${theme.primary}18`,
                        }}
                    >
                        <Sparkles size={13} />
                        {t.hero.badge}
                    </div>
                </div>

                {/* Headline */}
                <h1
                    className="hero-stagger"
                    style={{
                        fontSize: "clamp(2.7rem, 5.5vw, 4.4rem)",
                        fontWeight: 800,
                        color: theme.textPrimary,
                        marginBottom: "1.25rem",
                        lineHeight: 1.05,
                        letterSpacing: "-0.03em",
                        animationDelay: "90ms",
                    }}
                >
                    {t.hero.headlinePre}{" "}
                    <span style={{ position: "relative", display: "inline-block" }}>
                        <span
                            style={{
                                background: theme.textGradient,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                            }}
                        >
                            {t.hero.headlineHighlight}
                        </span>
                        {/* Hand-drawn underline flourish */}
                        <svg
                            aria-hidden
                            viewBox="0 0 220 14"
                            preserveAspectRatio="none"
                            style={{
                                position: "absolute",
                                left: "2%",
                                bottom: "-8px",
                                width: "96%",
                                height: "12px",
                                overflow: "visible",
                            }}
                        >
                            <path
                                d="M3 10 C 60 2, 160 2, 217 8"
                                fill="none"
                                stroke={theme.primary}
                                strokeWidth="5"
                                strokeLinecap="round"
                                opacity="0.65"
                            />
                        </svg>
                    </span>
                    {" "}{t.hero.headlinePost}
                    <br />
                    <span style={{ color: theme.textSecondary, fontWeight: 500, fontSize: "0.5em", letterSpacing: "0", fontFamily: "'Instrument Sans', sans-serif" }}>
                        {t.hero.subheadline}
                    </span>
                </h1>

                {/* Subtitle */}
                <p
                    className="hero-stagger"
                    style={{
                        fontSize: "1.08rem",
                        color: theme.textSecondary,
                        marginBottom: "2rem",
                        lineHeight: 1.75,
                        maxWidth: "480px",
                        animationDelay: "180ms",
                        ...(isMobile ? { margin: "0 auto 2rem auto" } : {}),
                    }}
                >
                    {t.hero.subtitleBefore}{" "}
                    <strong style={{ color: theme.textPrimary }}>{t.hero.dietModes}</strong>{" "}
                    {t.hero.subtitleAfter}
                </p>

                {/* CTA Buttons */}
                <div
                    className="hero-stagger"
                    style={{
                        display: "flex",
                        gap: "0.9rem",
                        marginBottom: "1.75rem",
                        flexWrap: "wrap" as const,
                        alignItems: "center",
                        justifyContent: isMobile ? "center" : "flex-start",
                        animationDelay: "270ms",
                    }}
                >
                    <a
                        href={APP_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            ...commonStyles.primaryBtn,
                            padding: "0.95rem 2rem",
                            fontSize: "1rem",
                            borderRadius: "14px",
                            boxShadow: `0 12px 32px ${theme.primaryGlow}`,
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.55rem",
                        }}
                        onMouseEnter={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px) scale(1.02)";
                            (e.currentTarget as HTMLAnchorElement).style.boxShadow = `0 20px 40px ${theme.primaryGlow}`;
                        }}
                        onMouseLeave={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0) scale(1)";
                            (e.currentTarget as HTMLAnchorElement).style.boxShadow = `0 12px 32px ${theme.primaryGlow}`;
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 814 1000" fill="currentColor">
                            <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 70.9 0 130.1 46.4 173.4 46.4 42.7 0 109.2-49 188.3-49 30.5 0 108.2 2.6 160.6 61.4zm-244-190.5c31.5-37 53.5-88.1 53.5-139.2 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.5-55.1 135.2 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.1-71.1z"/>
                        </svg>
                        {t.hero.downloadOnAppStore}
                    </a>

                    <a
                        href="#how-it-works"
                        onClick={scrollToHowItWorks}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.4rem",
                            padding: "0.95rem 1.5rem",
                            borderRadius: "14px",
                            border: `1.5px solid ${theme.cardBorder}`,
                            background: "rgba(255,255,255,0.7)",
                            color: theme.textPrimary,
                            fontSize: "1rem",
                            fontWeight: 600,
                            textDecoration: "none",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            backdropFilter: "blur(6px)",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = theme.primary;
                            e.currentTarget.style.background = "#FFFFFF";
                            e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = theme.cardBorder;
                            e.currentTarget.style.background = "rgba(255,255,255,0.7)";
                            e.currentTarget.style.transform = "translateY(0)";
                        }}
                    >
                        {t.hero.seeHowItWorks}
                        <ChevronDown size={16} />
                    </a>
                </div>

                {/* Social proof row */}
                <div
                    className="hero-stagger"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "2.25rem",
                        justifyContent: isMobile ? "center" : "flex-start",
                        animationDelay: "360ms",
                    }}
                >
                    <div style={{ display: "flex" }}>
                        {avatarEmojis.map((emoji, i) => (
                            <div
                                key={i}
                                style={{
                                    width: "34px",
                                    height: "34px",
                                    borderRadius: "50%",
                                    background: i % 2 === 0 ? `${theme.primary}20` : `${theme.secondary}18`,
                                    border: "2px solid #FFFFFF",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "16px",
                                    marginLeft: i === 0 ? 0 : "-10px",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                }}
                            >
                                {emoji}
                            </div>
                        ))}
                    </div>
                    <div>
                        <div style={{ display: "flex", gap: "2px", marginBottom: "2px" }}>
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} size={13} fill="#FBBF24" color="#FBBF24" />
                            ))}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: theme.textSecondary, fontWeight: 500 }}>
                            {t.hero.socialProof}
                        </div>
                    </div>
                </div>

                {/* Stats row */}
                <div
                    className="hero-stagger"
                    style={{
                        display: "inline-flex",
                        gap: isMobile ? "1.5rem" : "2.25rem",
                        justifyContent: isMobile ? "center" : "flex-start",
                        padding: "1rem 1.5rem",
                        background: "rgba(255,255,255,0.65)",
                        border: `1px solid ${theme.cardBorder}`,
                        borderRadius: "18px",
                        backdropFilter: "blur(6px)",
                        animationDelay: "450ms",
                    }}
                >
                    {stats.map((stat, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && (
                                <div style={{ width: "1px", background: theme.cardBorder, alignSelf: "stretch" }} />
                            )}
                            <div style={{ textAlign: isMobile ? "center" : "left" }}>
                                <div
                                    style={{
                                        fontSize: "1.55rem",
                                        fontWeight: 800,
                                        fontFamily: "'Bricolage Grotesque', sans-serif",
                                        background: theme.textGradient,
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        backgroundClip: "text",
                                        lineHeight: 1,
                                        marginBottom: "3px",
                                    }}
                                >
                                    {stat.number}
                                </div>
                                <div
                                    style={{
                                        fontSize: "0.72rem",
                                        color: theme.textSecondary,
                                        fontWeight: 600,
                                        textTransform: "uppercase" as const,
                                        letterSpacing: "0.07em",
                                    }}
                                >
                                    {stat.label}
                                </div>
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* ── Right Side – Phone Showcase ── */}
            <div
                className="hero-stagger"
                style={{
                    ...heroStyles.heroRight,
                    position: "relative",
                    zIndex: 1,
                    animationDelay: "250ms",
                    ...(isMobile ? heroStyles.mobileScreenshots : {}),
                }}
            >
                <ScreenshotsGrid theme={theme} isMobile={isMobile} />
            </div>
        </section>
    );
};

export default HeroSection;
